import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Container from '@material-ui/core/Container';
import IconButton from '@material-ui/core/IconButton';
import { useSnackbar } from 'notistack';

import AppBar from '../../components/AppBar/AppBar.connected';
import {
  fetchClientListData,
  fetchClientListNearestData,
  fetchMoreClientListNearestData,
} from '../../store/reducers/clients';
import CustomersList from './CustomersList';
import { fetchCurrentlyLoggedUser } from '../../store/actions/userActions';
import {
  SearchIcon,
  StyledButton,
  NoClientsTitle,
  Version,
} from './SelectCustomerView.styles';

const LOCATION_PERMISSION_DENIED = 1;

function fetchNearest(
  enqueueSnackbar,
  dispatch,
  t,
  page = 1,
  setNearestPage,
  onLocationError
) {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition((position) => {
      if (page === 1) {
        dispatch(
          fetchClientListNearestData({
            coords: position.coords,
            page,
          })
        );
      } else {
        dispatch(
          fetchMoreClientListNearestData({
            coords: position.coords,
            page,
          })
        );
      }
      setNearestPage(page + 1);
    }, onLocationError);
  } else {
    enqueueSnackbar(t('geo_not_supported', { variant: 'error' }));
  }
}

const SelectCustomerView = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [geolocationDenied, setGeolocationDenied] = useState(false);
  const [nearestPage, setNearestPage] = useState(1);

  useEffect(() => {
    dispatch(fetchClientListData());

    fetchNearest(
      enqueueSnackbar,
      dispatch,
      t,
      nearestPage,
      setNearestPage,
      (error) => {
        if (error.code === LOCATION_PERMISSION_DENIED) {
          setGeolocationDenied(true);
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, enqueueSnackbar, t, setGeolocationDenied]);

  const {
    clients,
    clientsFetched,
    nearestClients,
    totalNearestClients,
    nearestClientsFetched,
  } = useSelector((state) => state.clients);
  const { allowed } = useSelector((state) => state.user);

  const { clientData } = useSelector((state) => state);

  const [showMoreNearest, setShowMoreNearest] = useState(false);
  useEffect(() => {
    setShowMoreNearest(nearestClients?.length < totalNearestClients);
  }, [nearestClients, totalNearestClients]);

  const { online } = useSelector((state) => state.offline);

  const hasNearestClients = nearestClients && nearestClients.length > 0;
  const hasClients = clients && clients.length > 0;
  const hasAllowedTAs = allowed && allowed.length > 0;

  const isEmpty =
    !hasClients &&
    clientsFetched &&
    ((!hasNearestClients && nearestClientsFetched) || geolocationDenied);

  const handleShowMoreClick = () => {
    fetchNearest(
      enqueueSnackbar,
      dispatch,
      t,
      nearestPage,
      setNearestPage,
      (error) => {
        if (error.code === LOCATION_PERMISSION_DENIED) {
          setGeolocationDenied(true);
        }
      }
    );
    setShowMoreNearest(false);
  };

  useEffect(() => {
    dispatch(fetchCurrentlyLoggedUser());
  }, [dispatch]);

  return (
    <Container maxWidth="sm" data-testid="select-customer-view">
      <AppBar
        title={t('customer_list_title')}
        aside={
          (hasAllowedTAs || !isEmpty) && (
            <Link to="/customers/search" data-testid="search-icon">
              <IconButton>
                <SearchIcon />
              </IconButton>
            </Link>
          )
        }
      />
      {!isEmpty ? (
        <>
          {clientsFetched && (nearestClientsFetched || geolocationDenied) && (
            <>
              <CustomersList
                clientData={clientData && clientData.data}
                data-testid="clients-list"
                items={hasNearestClients ? nearestClients : clients}
                online={online}
                showPagination={!hasNearestClients}
              />
              {hasNearestClients && showMoreNearest && online ? (
                <StyledButton theme="primary" onClick={handleShowMoreClick}>
                  {t('customer_list_show_more')}
                </StyledButton>
              ) : null}
            </>
          )}
        </>
      ) : (
        <NoClientsTitle>{t('no_client_title')}</NoClientsTitle>
      )}
      <Version>v{process.env.REACT_APP_VERSION}</Version>
    </Container>
  );
};

export default SelectCustomerView;
